import { useQuery } from '@tanstack/react-query';
import { STATE_ABBREVIATIONS } from 'constants/stateAbbreviations';
import useUser from 'utils/useUser';
import dataService from 'services/dataService';
import { API_CLINICS } from 'services/dataService/resources';
import QueryParams from 'utils/queryParams';

import type { IClinic } from 'infrastructure/interfaces';

// Reverse lookup: abbreviation → full state name (e.g., "PA" → "Pennsylvania")
const ABBREVIATION_TO_STATE: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBREVIATIONS).map(([name, abbr]) => [abbr, name]),
);

/**
 * Normalize a state value to the canonical full name.
 * Accepts either a full name ("California") or abbreviation ("CA"/"ca").
 * Returns the original value if no match is found.
 */
const normalizeStateName = (value: string): string => {
  if (STATE_ABBREVIATIONS[value]) return value;
  return ABBREVIATION_TO_STATE[value.toUpperCase()] ?? value;
};

type ClinicWithPatientCount = IClinic & { patientCount?: number };

export interface IClinicForPod {
  guid: string;
  name: string;
  state: string;
  patientCount: number;
}

const CLINICS_FOR_POD_KEY = 'clinicsForPod';
const FIVE_MINUTES = 5 * 60 * 1000;

export const useClinicsForPod = () => {
  const user = useUser();

  const agencyGuid = user.agencies?.[0]?.guid;

  const {
    data: clinics = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: [CLINICS_FOR_POD_KEY, agencyGuid],
    enabled: !!agencyGuid || user.isAdmin,
    queryFn: async () => {
      const filtersString = QueryParams.stringify({
        ...(agencyGuid && { agencyGuid }),
        status: 'active',
        includePatientCount: true,
      });
      const { data, error } = await dataService.getList<
        ClinicWithPatientCount[]
      >(`${API_CLINICS}?${filtersString}`);

      if (error || !data) return [];

      return data.map(
        (item): IClinicForPod => ({
          guid: item.guid ?? '',
          name: item.legalname ?? '',
          state: normalizeStateName(item.state ?? ''),
          patientCount: item.patientCount ?? 0,
        }),
      );
    },
    staleTime: FIVE_MINUTES,
  });

  return { clinics, isLoading, isError };
};
